import {
  Col,
  type OperationType,
  RemovableCard,
  Row,
  SelectField,
} from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import { useProjectOptions } from '@/features/project/_hooks/project';
import {
  ProjectRelationTypeLabelModifier,
  projectRelationType,
} from '@/features/project/projects/_models/project';
import type { FC } from 'react';
import type { UseFieldArrayRemove } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getRequestSheetTypeByRelationType } from '../../../_utils/related-project';

type Props = {
  idx: number;
  removeRelatedProject: UseFieldArrayRemove;
  operation: OperationType;
  ignoredProjectIds?: string[];
};

export const RelatedProjectField: FC<Props> = ({
  idx,
  removeRelatedProject,
  ignoredProjectIds,
  operation,
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
    resetField,
  } = useProjectFormContext();
  const [fiscalYear, relatedProject] = watch([
    'data.fiscalYear',
    `data.relatedProjects.${idx}`,
  ]);

  const projectOptions = useProjectOptions({
    params: {
      fiscalYear: fiscalYear?.year().toString(),
      sheetType: getRequestSheetTypeByRelationType(
        relatedProject?.relationType,
      ),
      // シート種別に関係なく、関連事業としてレビュー対象外の事業を選択できないように制限
      skipAdministrativeReformTeamEvaluation: false,
      onlyEditable: true,
    },
    enabled: !!fiscalYear,
    select: (data) => {
      const filteredData =
        operation === 'edit' && ignoredProjectIds
          ? data.filter((option) => !ignoredProjectIds.includes(option.value))
          : data;
      return filteredData;
    },
  });
  return (
    <RemovableCard
      onRemove={() => {
        removeRelatedProject(idx);
      }}
    >
      <Row gutter={[24, 0]}>
        <Col span={12}>
          <SelectField
            name={`data.relatedProjects.${idx}.relationType`}
            label={t('related-project-relation-type')}
            control={control}
            errors={errors}
            options={projectRelationType.options.map((value) => ({
              value,
              label: ProjectRelationTypeLabelModifier[value],
            }))}
            shouldCoexistOnChange
            onChange={() => {
              resetField(`data.relatedProjects.${idx}.id`);
            }}
            required
          />
        </Col>

        <Col span={12}>
          <SelectField
            name={`data.relatedProjects.${idx}.id`}
            label={t('project-name')}
            control={control}
            errors={errors}
            options={projectOptions.data}
            showSearch
            required
            loading={projectOptions.isPending}
          />
        </Col>
      </Row>
    </RemovableCard>
  );
};
